
import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiV2 } from '../services/api';
import LocalStorageService from '../services/storage';

const localStorageService = LocalStorageService();

interface CostCenter {
    id_cost: string;
    costs_center_name: string;
    description: string;
    isactive?: boolean;

}
type CostCenterInput = Omit<CostCenter, 'id_cost'>

interface CostCentersProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface CostCentersContextData {
    createCostCenter: (costCenter: CostCenterInput) => Promise<ResponseProps>;
    updateCostCenter: (costCenter: CostCenter) => Promise<ResponseProps>;
    updateOnDelete: (id: string | number) => Promise<ResponseProps>;
}

interface UseCostCentersOptions {
    loadCostCenters?: boolean;
}

interface UseCostCentersData extends CostCentersContextData {
    costCenters: CostCenter[];
}

const CostCentersContext = createContext<CostCentersContextData>(
    {} as CostCentersContextData
);

const costCentersQueryKey = ['cost-centers'];
const emptyCostCenters: CostCenter[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

async function fetchCostCenters() {
  const response = await apiV2.get('cost-centers');
  return Array.isArray(response?.data) ? response.data : [];
}

export function CostCentersProvider({ children }: CostCentersProvidersProps) {
  const queryClient = useQueryClient();

  function invalidateCostCenters() {
    queryClient.invalidateQueries({ queryKey: costCentersQueryKey });
  }

  async function updateOnDelete(id: string | number) {
    const response = await apiV2.delete(`/cost-centers/${id}`);
    if (response.status === 200) {
      invalidateCostCenters();
      return {
        success: true,
        message: "Centro de custo removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover centro de custo."
    };
  }

  async function createCostCenter(costCenterInput: CostCenterInput) {
    const response = await apiV2.post('/cost-centers', costCenterInput);
    if (response.status === 201) {
      invalidateCostCenters();
      return {
        success: true,
        message: "Centro de custo adicionado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar centro de custo."
    };

  }

  async function updateCostCenter(costCenter: CostCenter) {
    const response = await apiV2.put(`/cost-centers/${costCenter.id_cost}`, costCenter);
    if (response.status === 204) {
      invalidateCostCenters();
      return {
        success: true,
        message: "Centro de custo editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar centro de custo."
    };
  }

  return (
    <CostCentersContext.Provider value={{ createCostCenter, updateCostCenter, updateOnDelete }}>
      {children}
    </CostCentersContext.Provider>
  );
}

export function UseCostCenters(options: UseCostCentersOptions = {}): UseCostCentersData {
  const context = useContext(CostCentersContext);
  const { loadCostCenters = false } = options;

  const costCentersQuery = useQuery({
    queryKey: costCentersQueryKey,
    queryFn: fetchCostCenters,
    enabled: loadCostCenters && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...context,
    costCenters: costCentersQuery.data || emptyCostCenters
  };
}
